import { type ReactNode, useEffect, useState } from 'react';
import { Wordmark } from './brand';
import { setThemePreference, type ThemePreference, useTheme } from './theme';

// Public-site header and footer, shared by marketing, request-access, and status.
// Apps must import "@inboxkit/ui/brand.css" (it also scans this package).

export interface SiteLink {
  label: string;
  href: string;
}

const ICONS: Record<ThemePreference, ReactNode> = {
  // sun, moon, and a half-filled circle for "system"
  light: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  dark: <path d="M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z" />,
  system: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" />
    </>
  ),
};

/** Cycles system → light → dark. */
export function ThemeToggle() {
  const { preference } = useTheme();
  const order: ThemePreference[] = ['system', 'light', 'dark'];
  const next = order[(order.indexOf(preference) + 1) % order.length] ?? 'system';
  return (
    <button
      type="button"
      onClick={() => setThemePreference(next)}
      aria-label={`Theme: ${preference}. Switch to ${next}`}
      title={`Theme: ${preference}`}
      className="glass grid h-8 w-8 place-items-center rounded-lg text-ink"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
        {ICONS[preference]}
      </svg>
    </button>
  );
}

/**
 * Floating header. Transparent over a hero, frosted glass once the page scrolls.
 * `solid` starts it in glass (for pages without a hero).
 */
export function SiteHeader({
  homeUrl,
  label,
  links = [],
  signInUrl,
  cta,
  solid = false,
}: {
  homeUrl: string;
  label?: string | undefined;
  links?: SiteLink[];
  signInUrl?: string | undefined;
  cta?: (SiteLink & { onClick?: () => void }) | undefined;
  solid?: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3">
      <div
        className={`mx-auto flex h-12 max-w-[1400px] items-center gap-6 rounded-2xl px-3 transition-all duration-500 ${
          solid || scrolled ? 'glass' : ''
        }`}
      >
        {/* flex, not inline: an inline link aligns the mark to its text line, 3px high. */}
        <a href={homeUrl} className="flex items-center text-ink">
          <Wordmark label={label} />
        </a>
        {links.length > 0 && (
          <nav className="hidden items-center gap-6 text-[13.5px] text-ink-soft md:flex">
            {links.map((l) => (
              <a key={l.label} href={l.href} className="transition-colors hover:text-ink">
                {l.label}
              </a>
            ))}
          </nav>
        )}
        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
          {signInUrl && (
            <a
              href={signInUrl}
              className="hidden rounded-lg px-3 py-1.5 text-[13.5px] text-ink-soft transition-colors hover:text-ink sm:block"
            >
              Sign in
            </a>
          )}
          {cta && (
            <a
              href={cta.href}
              onClick={cta.onClick}
              className="glass rounded-lg px-3.5 py-1.5 text-[13.5px] font-medium text-ink"
            >
              {cta.label}
            </a>
          )}
        </div>
      </div>
    </header>
  );
}

export function SiteFooter({
  columns,
  statusUrl,
  aside,
}: {
  columns: { title: string; links: SiteLink[] }[];
  statusUrl?: string | undefined;
  aside?: ReactNode;
}) {
  return (
    <footer className="border-t border-rule px-6 pt-16 pb-10">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-10 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div>
            <div className="text-ink">
              <Wordmark size="lg" divider={false} />
            </div>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-mute">Email inboxes, designed for agents.</p>
            {statusUrl && (
              <a
                href={statusUrl}
                className="glass mt-6 inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono text-[11px] text-ink-soft"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-leaf" />
                System status
              </a>
            )}
            {aside}
          </div>
          {columns.map((c) => (
            <div key={c.title}>
              <div className="font-mono text-[11px] tracking-[0.16em] text-mute uppercase">{c.title}</div>
              <ul className="mt-4 space-y-2.5 text-sm">
                {c.links.map((l) => (
                  <li key={l.label}>
                    <a href={l.href} className="text-ink-soft transition-colors hover:text-ink">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-rule pt-8 text-xs text-mute">
          <p>© {new Date().getFullYear()} Automata HQ</p>
          <div className="spectrum h-1 w-24 rounded-full opacity-70" />
        </div>
      </div>
    </footer>
  );
}

